/**
 * CodigosPage - Página principal del módulo de Códigos de Ética
 * Features:
 * - Lista de códigos de ética
 * - Generar nuevo código
 * - Ver versiones
 * - Gestión de estados
 * - Historial por organización
 */
import { useState, useEffect } from 'react';
import { 
  Plus, 
  FileText, 
  Eye, 
  Edit,
  CheckCircle,
  Archive,
  Clock,
  Calendar,
  History,
  TrendingUp
} from 'lucide-react';
import { codigosAPI } from '../../api';
import useAuthStore from '../../store/authStore';
import {
  Button,
  Card,
  Table,
  Pagination,
  Loading,
  Alert,
  Modal,
} from '../../components/common';
import CodigoGenerator from '../../components/codigos/CodigoGenerator';
import CodigoViewer from '../../components/codigos/CodigoViewer';
import CodigoApproval from '../../components/codigos/CodigoApproval';

const CodigosPage = () => {
  const { user } = useAuthStore();
  const isAdmin = user?.rol === 'superadmin' || user?.rol === 'admin_org';

  // Estado
  const [codigos, setCodigos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  // Filtros
  const [filters, setFilters] = useState({
    estado: '',
    fecha_desde: '',
    fecha_hasta: '',
  });

  // Modales
  const [showGeneratorModal, setShowGeneratorModal] = useState(false);
  const [showViewerModal, setShowViewerModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedCodigo, setSelectedCodigo] = useState(null);
  const [historial, setHistorial] = useState([]);

  // Estadísticas
  const [estadisticas, setEstadisticas] = useState(null);

  // Cargar códigos
  const loadCodigos = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
      };

      // Aplicar filtros
      if (filters.estado) {
        params.estado = filters.estado;
      }
      if (filters.fecha_desde) {
        params.fecha_desde = filters.fecha_desde;
      }
      if (filters.fecha_hasta) {
        params.fecha_hasta = filters.fecha_hasta;
      }

      const response = await codigosAPI.getAll(params);
      
      setCodigos(response.data.items || response.data);
      setTotalItems(response.data.total || response.data.length);
      setTotalPages(Math.ceil((response.data.total || response.data.length) / itemsPerPage));

      // Cargar estadísticas si es admin
      if (isAdmin && user?.id_organizacion) {
        loadEstadisticas();
      }
    } catch (err) {
      console.error('Error al cargar códigos:', err);
      setError(err.message || 'Error al cargar códigos');
    } finally {
      setLoading(false);
    }
  };

  const loadEstadisticas = async () => {
    try {
      const response = await codigosAPI.getEstadisticas(user.id_organizacion);
      setEstadisticas(response.data);
    } catch (err) {
      console.error('Error al cargar estadísticas:', err);
    }
  };

  useEffect(() => {
    loadCodigos();
  }, [currentPage, filters]);

  // Handlers
  const handleGenerarCodigo = () => {
    setShowGeneratorModal(true);
  };

  const handleCodigoGenerado = () => {
    setShowGeneratorModal(false);
    setSuccess('Código de ética generado correctamente');
    loadCodigos();
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleVerCodigo = (codigo) => {
    setSelectedCodigo(codigo);
    setShowViewerModal(true);
  };

  const handleEditarCodigo = (codigo) => {
    // TODO: Implementar edición
    alert('Función de edición en desarrollo');
  };

  const handleAprobar = (codigo) => {
    setSelectedCodigo(codigo);
    setShowApprovalModal(true);
  };

  const handleAprobacionCompletada = () => {
    setShowApprovalModal(false);
    setSuccess('Código aprobado correctamente');
    loadCodigos();
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleVerHistorial = async () => {
    try {
      setLoading(true);
      const response = await codigosAPI.getHistorial(user.id_organizacion);
      setHistorial(response.data);
      setShowHistoryModal(true);
    } catch (err) {
      console.error('Error al cargar historial:', err);
      setError(err.message || 'Error al cargar historial');
    } finally {
      setLoading(false);
    }
  };

  const handleCambiarEstado = async (codigo, nuevoEstado) => {
    try {
      await codigosAPI.cambiarEstado(codigo.id_codigo, { estado: nuevoEstado });
      setSuccess(`Código ${nuevoEstado} correctamente`);
      loadCodigos();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error al cambiar estado:', err);
      setError(err.message || 'Error al cambiar estado');
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters });
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      estado: '',
      fecha_desde: '',
      fecha_hasta: '',
    });
    setCurrentPage(1);
  };

  // Obtener info de estado
  const getEstadoInfo = (estado) => {
    const estados = {
      borrador: {
        label: 'Borrador',
        color: 'yellow',
        icon: Clock,
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
      },
      activo: {
        label: 'Activo',
        color: 'green',
        icon: CheckCircle,
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
      },
      archivado: {
        label: 'Archivado',
        color: 'gray',
        icon: Archive,
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-800',
      },
    };

    return estados[estado] || estados.borrador;
  };

  // Columnas de la tabla
  const columns = [
    {
      key: 'nombre',
      label: 'Código',
      render: (item) => (
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-gray-400" />
          <div>
            <span className="font-medium text-gray-900">{item.nombre}</span>
            <p className="text-sm text-gray-500">Versión {item.version}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'fecha',
      label: 'Fecha Generación',
      render: (item) => (
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-400" />
          <span className="text-sm">
            {new Date(item.fecha_generacion).toLocaleDateString('es-ES')}
          </span>
        </div>
      ),
    },
    {
      key: 'basado_en',
      label: 'Basado En',
      render: (item) => (
        <div className="text-sm text-gray-600">
          {item.id_evaluacion_chequeo && <div>✓ Lista de Chequeo</div>}
          {item.id_instancia_encuesta && <div>✓ Encuesta</div>}
        </div>
      ),
    },
    {
      key: 'estado',
      label: 'Estado',
      render: (item) => {
        const estadoInfo = getEstadoInfo(item.estado);
        const Icon = estadoInfo.icon;
        
        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${estadoInfo.bgColor} ${estadoInfo.textColor}`}
          >
            <Icon className="h-3 w-3 mr-1" />
            {estadoInfo.label}
          </span>
        );
      },
    },
    {
      key: 'aprobacion',
      label: 'Aprobación',
      render: (item) => {
        if (!item.aprobado_por) {
          return (
            <span className="text-sm text-gray-400">Pendiente</span>
          );
        }
        return (
          <div className="text-sm">
            <div className="text-green-600 font-medium">✓ Aprobado</div>
            <div className="text-gray-500 text-xs">
              {new Date(item.fecha_aprobacion).toLocaleDateString('es-ES')}
            </div>
          </div>
        );
      },
    },
    {
      key: 'acciones',
      label: 'Acciones',
      render: (item) => (
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleVerCodigo(item)}
          >
            <Eye className="h-4 w-4" />
          </Button>
          
          {isAdmin && item.estado === 'borrador' && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleEditarCodigo(item)}
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleAprobar(item)}
              >
                Aprobar
              </Button>
            </>
          )}

          {isAdmin && item.estado === 'activo' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleCambiarEstado(item, 'archivado')}
            >
              Archivar
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Códigos de Ética</h1>
          <p className="mt-1 text-sm text-gray-500">
            Generación y gestión de códigos de ética organizacionales
          </p>
        </div>
        <div className="flex gap-3">
          {isAdmin && (
            <>
              <Button
                variant="outline"
                onClick={handleVerHistorial}
                icon={History}
              >
                Historial
              </Button>
              <Button
                variant="primary"
                onClick={handleGenerarCodigo}
                icon={Plus}
              >
                Generar Código
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Alertas */}
      {error && (
        <Alert
          type="error"
          title="Error"
          message={error}
          onClose={() => setError(null)}
        />
      )}

      {success && (
        <Alert
          type="success"
          title="Éxito"
          message={success}
          onClose={() => setSuccess(null)}
        />
      )}

      {/* Estadísticas */}
      {estadisticas && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Códigos</p>
                <p className="text-2xl font-bold">{estadisticas.total_codigos || 0}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Activos</p>
                <p className="text-2xl font-bold">{estadisticas.codigos_activos || 0}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Borradores</p>
                <p className="text-2xl font-bold">{estadisticas.codigos_borrador || 0}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Versión Actual</p>
                <p className="text-2xl font-bold">{estadisticas.version_actual || 'N/A'}</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Filtros */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estado
            </label>
            <select
              value={filters.estado}
              onChange={(e) => handleFilterChange({ estado: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Todos</option>
              <option value="borrador">Borradores</option>
              <option value="activo">Activos</option>
              <option value="archivado">Archivados</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Desde
            </label>
            <input
              type="date"
              value={filters.fecha_desde}
              onChange={(e) => handleFilterChange({ fecha_desde: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Hasta
            </label>
            <input
              type="date"
              value={filters.fecha_hasta}
              onChange={(e) => handleFilterChange({ fecha_hasta: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="flex items-end">
            <Button variant="outline" onClick={clearFilters} className="w-full">
              Limpiar Filtros
            </Button>
          </div>
        </div>
      </Card>

      {/* Tabla de códigos */}
      <Card>
        {loading ? (
          <div className="p-8">
            <Loading size="lg" text="Cargando códigos de ética..." />
          </div>
        ) : codigos.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No hay códigos de ética</p>
            {isAdmin && (
              <Button
                variant="primary"
                onClick={handleGenerarCodigo}
                className="mt-4"
              >
                Generar Primer Código
              </Button>
            )}
          </div>
        ) : (
          <>
            <Table columns={columns} data={codigos} />
            {totalPages > 1 && (
              <div className="border-t p-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </>
        )}
      </Card>

      {/* Modal: Generador */}
      <Modal
        isOpen={showGeneratorModal}
        onClose={() => setShowGeneratorModal(false)}
        title="Generar Código de Ética"
        size="xl"
      >
        <CodigoGenerator
          onSuccess={handleCodigoGenerado}
          onCancel={() => setShowGeneratorModal(false)}
        />
      </Modal>

      {/* Modal: Visualizador */}
      {selectedCodigo && (
        <Modal
          isOpen={showViewerModal}
          onClose={() => {
            setShowViewerModal(false);
            setSelectedCodigo(null);
          }}
          title={selectedCodigo.nombre}
          size="full"
        >
          <CodigoViewer codigoId={selectedCodigo.id_codigo} />
        </Modal>
      )}

      {/* Modal: Aprobación */}
      {selectedCodigo && (
        <Modal
          isOpen={showApprovalModal}
          onClose={() => {
            setShowApprovalModal(false);
            setSelectedCodigo(null);
          }}
          title="Aprobar Código de Ética"
          size="lg"
        >
          <CodigoApproval
            codigoId={selectedCodigo.id_codigo}
            onSuccess={handleAprobacionCompletada}
            onCancel={() => {
              setShowApprovalModal(false);
              setSelectedCodigo(null);
            }}
          />
        </Modal>
      )}

      {/* Modal: Historial */}
      <Modal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        title="Historial de Códigos"
        size="lg"
      >
        <div className="space-y-4">
          {historial.map((codigo) => (
            <Card key={codigo.id_codigo} className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">{codigo.nombre}</h4>
                  <p className="text-sm text-gray-500">
                    Versión {codigo.version} - {new Date(codigo.fecha_generacion).toLocaleDateString('es-ES')}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleVerCodigo(codigo)}
                >
                  Ver
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default CodigosPage;
